import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Stack,
  Typography,
  Tooltip,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';

interface OptionModalProps {
  showOptionModal: boolean;
  onCancel: () => void;
  onSubmit: (options: string[], columnInfo: any) => void;
  options: string[];
  columnInfo: any; // expects { attributeType, attribute, attrIndex } at minimum
}

const HEX = /^#[0-9A-Fa-f]{6}$/;

const OptionModal: React.FC<OptionModalProps> = ({
  showOptionModal,
  onCancel,
  onSubmit,
  options,
  columnInfo,
}) => {
  // editable option texts
  const [optionList, setOptionList] = useState<string[]>(['']);
  // per-option color map (keyed by option text)
  const [optionColors, setOptionColors] = useState<Record<string, string>>({});
  const { showSnackBar } = useSnackBar();

  // initialize on open
  useEffect(() => {
    if (showOptionModal) {
      const initial = options && options.length > 0 ? [...options] : [''];
      setOptionList(initial);

      const existingColors =
        columnInfo?.attribute?.option_colors &&
        typeof columnInfo.attribute.option_colors === 'object'
          ? (columnInfo.attribute.option_colors as Record<string, string>)
          : {};
      setOptionColors({ ...existingColors });
    }
  }, [showOptionModal, options, columnInfo]);

  const handleInputChange = (value: string, index: number) => {
    setOptionList((prev) => {
      const next = [...prev];
      const old = next[index];
      next[index] = value;

      // if the option text changed, move any existing color to the new key
      if (old && old !== value && optionColors[old]) {
        setOptionColors((prevColors) => {
          const c = { ...prevColors };
          const oldColor = c[old];
          delete c[old];
          if (value) c[value] = oldColor;
          return c;
        });
      }

      return next;
    });
  };

  const handleRemoveOption = (index: number) => {
    setOptionList((prev) => {
      const next = [...prev];
      const removed = next.splice(index, 1)[0];

      // drop its color if any
      if (removed && optionColors[removed]) {
        setOptionColors((prevColors) => {
          const c = { ...prevColors };
          delete c[removed];
          return c;
        });
      }

      return next.length > 0 ? next : [''];
    });
  };

  const addOptionRow = () => {
    setOptionList((prev) => [...prev, '']);
  };

  const handlePickColor = (optText: string, color: string) => {
    // store even if not valid hex; we’ll validate on save
    setOptionColors((prev) => ({ ...prev, [optText]: color }));
  };

  // simple dup/empty check for UX
  const optionsError = useMemo(() => {
    const cleaned = optionList.map((o) => o.trim()).filter(Boolean);
    const seen = new Set<string>();
    for (const o of cleaned) {
      if (seen.has(o)) return 'Duplicate options are not allowed.';
      seen.add(o);
    }
    return '';
  }, [optionList]);

  const handleSave = () => {
    const cleaned = optionList.map((o) => o.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      showSnackBar('Please enter at least one option.', 'error');
      return;
    }
    const unique = Array.from(new Set(cleaned));
    if (unique.length !== cleaned.length) {
      showSnackBar('Duplicate options are not allowed.', 'error');
      return;
    }

    // only send valid hex for options that still exist
    const colorsToSend = Object.fromEntries(
      unique
        .map((k) => [k, optionColors[k]] as const)
        .filter(([_, v]) => typeof v === 'string' && HEX.test(v))
    );

    const infoToSend = {
      ...columnInfo,
      option_colors: colorsToSend, // <-- important: pass colors back here
    };

    onSubmit(unique, infoToSend);
  };

  return (
    <Dialog open={showOptionModal} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6">Add Options</Typography>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {optionList.map((opt, index) => {
            const colorValue = optionColors[opt] || '#2196f3'; // default shown in picker
            const inputId = `opt-color-${index}`;
            return (
              // <Box key={`${index}-${opt}`} display="flex" alignItems="center" gap={2}>
              <Box key={index} display="flex" alignItems="center" gap={2}>
                <TextField
                  size="small"
                  fullWidth
                  label={`Option ${index + 1}`}
                  value={opt}
                  onChange={(e) => handleInputChange(e.target.value, index)}
                />

                <Tooltip title="Pick color for this option">
                  <Box display="flex" alignItems="center" gap={1}>
                    <ColorLensIcon fontSize="small" />
                    <input
                      id={inputId}
                      type="color"
                      value={HEX.test(colorValue) ? colorValue : '#2196f3'}
                      onChange={(e) => handlePickColor(opt, e.target.value)}
                      style={{
                        width: 32,
                        height: 28,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                    />
                  </Box>
                </Tooltip>

                {optionList.length > 1 && index !== 0 && (
                  <IconButton aria-label="remove" onClick={() => handleRemoveOption(index)}>
                    <CloseIcon />
                  </IconButton>
                )}
                {index === optionList.length - 1 && (
                  <IconButton aria-label="add" onClick={addOptionRow}>
                    <AddIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}

          {optionsError && (
            <Typography variant="caption" color="error">
              {optionsError}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" size="small" onClick={handleSave}>
          Submit
        </Button>
        <Button onClick={onCancel} size="small">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OptionModal;
